import React, { useState, useEffect } from 'react';
import TeamNode from './TeamTree';
import './ParentComponent2.css';
import Header2 from './Header2';
import Filters3 from './Filters3';
import AchievementTable from './AchievementTable';
import CustomButton from './Button2';

const ParentComponent2 = ({ onLogout }) => {
  const [stateTrees, setStateTrees] = useState({});
  const [error, setError] = useState("");
  const [selectedPid, setSelectedPid] = useState(null);
  const [selectedManager, setSelectedManager] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // ⭐ TREE ONLY DATE
  const [treeDate, setTreeDate] = useState("");

  // ⭐ NO-DATA message
  const [noDataMessage, setNoDataMessage] = useState("");

  // ⭐ LOADING STATE FOR TREE
  const [treeLoading, setTreeLoading] = useState(false);

  // ⭐ Set Yesterday Default
  useEffect(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const formatted = yesterday.toISOString().slice(0, 10);
    setTreeDate(formatted);
  }, []);

  // Last Updated Timestamp
  useEffect(() => {
    const today = new Date();
    today.setHours(6, 0, 0, 0);
    const formatted = today.toISOString().slice(0, 16).replace("T", " ");
    setLastUpdated(`Last data updated: ${formatted}`);
  }, []);

  // Helper to locate manager subtree
  const findManagerSubtree = (node, managerName) => {
    if (node.name === managerName || node.id === managerName) return node;
    if (!node.children) return null;

    for (const child of node.children) {
      const result = findManagerSubtree(child, managerName);
      if (result) return result;
    }
    return null;
  };

  // ⭐ INTERNAL TREE FETCH FUNCTION
  const fetchTreeDataInternal = async (pid, manager, date) => {
    try {
      setTreeLoading(true); // start loading
      setNoDataMessage("");

      const response = await fetch(
        `https://api-usersreport-insights.haqdarshak.com/api/tree?pid=${encodeURIComponent(pid)}${date ? `&date=${date}` : ""}`
      );

      if (!response.ok) throw new Error("Failed to fetch team data");

      const data = await response.json();

      if (data._noDataMessage) {
        setNoDataMessage(data._noDataMessage);
        setStateTrees({}); // ✅ IMPORTANT
        setTreeLoading(false);
        return;
      }

      // Filter by manager
      if (manager) {
        const filtered = {};
        Object.entries(data).forEach(([orgId, treeOrArray]) => {
          const arr = Array.isArray(treeOrArray) ? treeOrArray : [treeOrArray];
          for (const node of arr) {
            const match = findManagerSubtree(node, manager);
            if (match) {
              filtered[orgId] = match;
              
            }
          }
        });

        setStateTrees(filtered);
      } else {
        setStateTrees(data);
      }

      setError("");
      setTreeLoading(false);
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong");
      setTreeLoading(false);
    }
  };

  // ⭐ FILTERS (PID + MANAGER + TABLE DATES) — DO NOT TOUCH TREE DATE
  const fetchTreeDataFromFilters = async ({ pid, manager, startDate, endDate }) => {
    if (!pid) {
      setError("Please select a PID.");
      return;
    }

    const pidChanged = pid !== selectedPid;
    const managerChanged = manager !== selectedManager;

    setSelectedPid(pid);
    setSelectedManager(manager);
    setStartDate(startDate || "");
    setEndDate(endDate || "");

    if (pidChanged || managerChanged) {
      fetchTreeDataInternal(pid, manager, treeDate);
    }
  };

  // ⭐ TREE DATE CHANGE ONLY
  const handleTreeDateChange = (date) => {
    setTreeDate(date);
    if (selectedPid) {
      fetchTreeDataInternal(selectedPid, selectedManager, date);
    }
  };

  // ⭐ Load Tree FIRST time when yesterday is set
  useEffect(() => {
    if (treeDate && selectedPid) {
      fetchTreeDataInternal(selectedPid, selectedManager, treeDate);
    }
  }, [treeDate]);

  return (
    <>
      <Header2 onLogout={onLogout} />
      <nav className="header-nav-v2">
        <CustomButton />
      </nav>

      {/* Filters for PID + MANAGER + TABLE dates */}
      <Filters3 onFetch={fetchTreeDataFromFilters} />

      <div className="last-updated">{lastUpdated}</div>

      {selectedPid && (
        <AchievementTable
          pid={selectedPid}
          manager={selectedManager}
          startDate={startDate}
          endDate={endDate}
        />
      )}

      {error && <p style={{ color: "red" }}>{error}</p>}

      {/* Tree Section */}
      {selectedPid ? (
  Object.keys(stateTrees).length > 0 ? (
    Object.entries(stateTrees).map(([state, treeOrTrees]) => (
      <div key={state} style={{ marginBottom: "2rem" }}>
        <h3 style={{ textAlign: "center" }}>{state}</h3>

        <TeamNode
          data={treeOrTrees}
          onDateChange={handleTreeDateChange}
          noDataMessage={noDataMessage}
          treeLoading={treeLoading}
          defaultDate={treeDate}
        />
      </div>
    ))
  ) : (
    // ⭐ IMPORTANT: still render TeamTree even if no data
    <TeamNode
      data={null}
      onDateChange={handleTreeDateChange}
      noDataMessage={noDataMessage || "No data available for selected date"}
      treeLoading={treeLoading}
      defaultDate={treeDate}
    />
  )
) : (
  <div style={{ padding: "2rem", textAlign: "center", fontSize: "1.3rem" }}>
    No tree data loaded yet.
  </div>
)}
    </>
  );
};

export default ParentComponent2;
